/** localStorage, wrapped: private windows and blocked storage must never crash the app. */
export function getPref(key: string): string | null {
  try {
    return localStorage.getItem(`homes.${key}`);
  } catch {
    return null;
  }
}

export function setPref(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(`homes.${key}`);
    else localStorage.setItem(`homes.${key}`, value);
  } catch {
    /* ignore */
  }
}

/** 'house' (default): follow the current home's night-mode helper; falls back to the system theme. */
export type ThemePref = 'house' | 'system' | 'light' | 'dark';
export type Effective = 'light' | 'dark' | 'system';

export function getTheme(): ThemePref {
  const t = getPref('theme');
  return t === 'light' || t === 'dark' || t === 'system' ? t : 'house';
}

const listeners = new Set<() => void>();
export function onThemePref(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function applyTheme(t: ThemePref): void {
  setPref('theme', t === 'house' ? null : t);
  if (t !== 'house') setEffectiveTheme(t);
  for (const l of listeners) l();
}

let applied: Effective | null = null;
/** Sets <html data-theme>, cross-fading colours unless the user prefers reduced motion. */
export function setEffectiveTheme(t: Effective): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const cur = root.getAttribute('data-theme') ?? 'system';
  if (cur === t) {
    applied = t;
    return;
  }
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (applied !== null && !reduce) {
    root.classList.add('theme-fade');
    window.setTimeout(() => root.classList.remove('theme-fade'), 600);
  }
  applied = t;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
}

export const lastHome = {
  get: (): string | null => getPref('lastHome'),
  set: (home: string): void => setPref('lastHome', home),
};
